import { useCallback, useEffect, useRef, useState } from "react";
import DrivingSketchPad from "@/components/sketch/DrivingSketchPad";

const MAX_PAGES = 20;

function emptyPage(index) {
  return { page_index: index, title: "", template_id: "blank", strokes_json: "" };
}

function normalizePages(initialPages, initialStrokesJson, initialTemplateId) {
  if (Array.isArray(initialPages) && initialPages.length) {
    return initialPages.map((p, i) => ({
      page_index: p.page_index ?? i,
      title: p.title || "",
      template_id: p.template_id || "blank",
      strokes_json: p.strokes_json || (i === 0 ? initialStrokesJson || "" : ""),
    }));
  }
  return [
    {
      page_index: 0,
      title: "",
      template_id: initialTemplateId || "blank",
      strokes_json: initialStrokesJson || "",
    },
  ];
}

/**
 * Multi-page sketchbook — wraps DrivingSketchPad with page navigation and autosave-on-switch.
 */
export default function MultiPageSketchPad({
  initialPages,
  initialStrokesJson = "",
  initialTemplateId = "blank",
  onSave,
  saving = false,
  readOnly = false,
  saveLabel = "Save sketch",
  variant = "default",
}) {
  const isAudit = variant === "audit";
  const [pages, setPages] = useState(() =>
    normalizePages(initialPages, initialStrokesJson, initialTemplateId)
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const pageDraftRef = useRef({});

  useEffect(() => {
    setPages(normalizePages(initialPages, initialStrokesJson, initialTemplateId));
    setActiveIndex(0);
    pageDraftRef.current = {};
  }, [initialPages, initialStrokesJson, initialTemplateId]);

  const activePage = pages[activeIndex] || pages[0];

  const persistActiveDraft = useCallback(
    (draft) => {
      if (!draft) return;
      setPages((prev) =>
        prev.map((p, i) =>
          i === activeIndex
            ? {
                ...p,
                strokes_json: draft.strokes_json ?? p.strokes_json,
                template_id: draft.template_id ?? p.template_id,
              }
            : p
        )
      );
    },
    [activeIndex]
  );

  const switchPage = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= pages.length || nextIndex === activeIndex) return;
    const draft = pageDraftRef.current[activeIndex];
    if (draft) persistActiveDraft(draft);
    setActiveIndex(nextIndex);
  };

  const addPage = () => {
    if (pages.length >= MAX_PAGES) return;
    const draft = pageDraftRef.current[activeIndex];
    if (draft) persistActiveDraft(draft);
    const next = [...pages, emptyPage(pages.length)];
    setPages(next);
    setActiveIndex(next.length - 1);
  };

  const deletePage = () => {
    if (pages.length <= 1) return;
    if (!window.confirm("Delete this sketch page? This cannot be undone.")) return;
    setPages((prev) => {
      const next = prev.filter((_, i) => i !== activeIndex).map((p, i) => ({ ...p, page_index: i }));
      return next.length ? next : [emptyPage(0)];
    });
    setActiveIndex((i) => Math.max(0, i - 1));
    pageDraftRef.current = {};
  };

  const updateTitle = (title) => {
    setPages((prev) => prev.map((p, i) => (i === activeIndex ? { ...p, title } : p)));
  };

  const handleSave = (payload) => {
    const draft = pageDraftRef.current[activeIndex];
    const merged = pages.map((p, i) => {
      if (i !== activeIndex) return p;
      return {
        ...p,
        strokes_json: payload.strokes_json ?? draft?.strokes_json ?? p.strokes_json,
        template_id: payload.template_id ?? draft?.template_id ?? p.template_id,
      };
    });
    const activeStrokes = merged[activeIndex]?.strokes_json || "";
    onSave?.({
      ...payload,
      pages: merged,
      title: merged[activeIndex]?.title || "",
      strokes_json: activeStrokes,
      template_id: merged[activeIndex]?.template_id || merged[0]?.template_id || "blank",
    });
  };

  const navBtnClass = isAudit ? "audit-btn-compact" : "btn-outline min-h-11 px-3";

  return (
    <div className="space-y-3" data-testid="multipage-sketch-pad">
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="sketch-page-nav">
        {isAudit && (
          <input
            className="flex-1 min-w-[8rem] px-3 py-2 border rounded text-sm"
            style={{ borderColor: "rgba(22,25,28,.2)" }}
            value={activePage.title || ""}
            onChange={(e) => updateTitle(e.target.value)}
            disabled={readOnly}
            placeholder="Untitled page"
            aria-label="Page title"
            data-testid="sketch-page-title"
          />
        )}
        <button
          type="button"
          className={navBtnClass}
          style={isAudit ? { marginLeft: 0 } : undefined}
          onClick={() => switchPage(activeIndex - 1)}
          disabled={activeIndex === 0 || readOnly}
          data-testid="sketch-prev-page"
        >
          ‹ Previous
        </button>
        <span className="font-medium">
          Page {activeIndex + 1} of {pages.length}
        </span>
        <button
          type="button"
          className={navBtnClass}
          style={isAudit ? { marginLeft: 0 } : undefined}
          onClick={() => switchPage(activeIndex + 1)}
          disabled={activeIndex >= pages.length - 1 || readOnly}
          data-testid="sketch-next-page"
        >
          Next ›
        </button>
        <button
          type="button"
          className={navBtnClass}
          style={isAudit ? { marginLeft: 0 } : undefined}
          onClick={addPage}
          disabled={readOnly || pages.length >= MAX_PAGES}
          data-testid="sketch-add-page"
        >
          + Add Page
        </button>
        <button
          type="button"
          className={`${navBtnClass} text-destructive`}
          style={isAudit ? { marginLeft: 0 } : undefined}
          onClick={deletePage}
          disabled={readOnly || pages.length <= 1}
          data-testid="sketch-delete-page"
        >
          Delete Page
        </button>
      </div>

      {!isAudit && (
        <label className="block space-y-1 text-sm">
          <span>Page title (optional)</span>
          <input
            className="input w-full"
            value={activePage.title || ""}
            onChange={(e) => updateTitle(e.target.value)}
            disabled={readOnly}
            data-testid="sketch-page-title-fallback"
          />
        </label>
      )}

      <div className="flex flex-wrap gap-1" data-testid="sketch-page-thumbs">
        {pages.map((p, i) => (
          <button
            key={`page-${i}`}
            type="button"
            className={`min-h-11 px-2 py-1 rounded border text-xs ${i === activeIndex ? "bg-primary text-primary-foreground" : "btn-outline"}`}
            onClick={() => switchPage(i)}
            disabled={readOnly && i !== activeIndex}
            data-testid={`sketch-page-thumb-${i}`}
          >
            {i + 1}
            {p.title ? `: ${p.title.slice(0, 12)}` : ""}
          </button>
        ))}
      </div>

      <DrivingSketchPad
        key={`sketch-page-${activeIndex}`}
        templateId={activePage.template_id}
        initialStrokesJson={activePage.strokes_json}
        onDraftChange={(draft) => {
          pageDraftRef.current[activeIndex] = draft;
        }}
        onSave={handleSave}
        saving={saving}
        readOnly={readOnly}
        saveLabel={saveLabel}
        compactTemplates={isAudit}
      />
    </div>
  );
}
